import { V } from "../../app/pages.js";
import { S, ui } from "../../app/store.js";
import {
  chip,
  csvBtn,
  panel,
  periodSeg,
  seg,
  ssn,
  tile,
} from "../../components/ui.js";
import {
  can,
  canCompany,
  canInvoice,
  me,
  uname,
} from "../../services/access.js";
import { accInvoices } from "../invoices/page.js";
import { invoiceSheet } from "../invoices/components.js";
import { invoicePartySummary } from "../invoices/identity.js";
import { invoiceParties } from "../../domain/invoicing.js";
import {
  addDays,
  daysTo,
  fmtD,
  qEnd,
  quarterOf,
  shortD,
  today,
} from "../../domain/dates.js";
import { P, esc, pad2, peso, pesoShort } from "../../domain/format.js";
import { client } from "../invoices/model.js";
import { depActiveIn } from "../deployments/page.js";
import { depPay, runTot } from "../../domain/payroll.js";
import { proj } from "../sites/model.js";
import { birCalendar, taxFigures } from "../../domain/compliance.js";
import { ACC, ACCOUNTS, COS, TIN_RE } from "../../domain/reference.js";
import { bal, journalOf, natural } from "../../domain/ledger.js";
import { jvForm } from "./voucher-form.js";
import { periodRange } from "../../services/filters.js";

const ACC_TABS = [
  ["overview", "Overview"],
  ["invoices", "Invoices"],
  ["books", "Books"],
  ["bir", "BIR forms"],
];

const ACC_SUB = {
  overview: "What needs doing in the books, and the numbers behind it.",
  invoices:
    "Sales invoices in one gapless series. Every invoice prints Milltech's and the client's TIN and business address.",
  books: "BIR, Books & Owner's Copies",
  bir: "The returns Milltech's registration requires, with due dates and the amounts from the books.",
};

const BOOK_SECTIONS = [
  ["copies", "Owner's Copies (INV)"],
  ["withholding", "Withholding (2307)"],
  ["ledger", "Double-Entry Ledger"],
  ["payroll", "Payroll & Statutory"],
  ["position", "Tax Position"],
  ["audit", "Audit Trail"],
];

const BOOK_INTRO = {
  copies:
    "Every sales invoice issued, numbered in a gapless series. This is the sales book BIR expects.",
  withholding:
    "Tax clients withheld from Milltech's fees, and the BIR Form 2307 each owes.",
  ledger:
    "Double-entry books. Invoices, client payments and payroll post themselves.",
  payroll:
    "What approved payroll runs owe SSS, PhilHealth, Pag-IBIG and the BIR this month.",
  position:
    "What Milltech owes in VAT and income tax for the quarter, read from the same books.",
  audit: "Every action with who did it and when.",
};

const LEDGER_VIEWS = [
  ["chart", "Chart of Accounts"],
  ["vouchers", "Journal Vouchers"],
  ["books", "Journal Books"],
  ["activity", "Account Activity"],
  ["tb", "Trial Balance"],
  ["is", "Income Statement"],
  ["bs", "Balance Sheet"],
];

const LEDGER_INTRO = {
  chart:
    "Every account Milltech posts to. Invoices, payments and payroll post to their accounts automatically.",
  vouchers:
    "Record what invoices, payments and payroll don't: accruals, adjustments, owner's capital.",
  books: "The statutory books: every entry, by journal.",
  activity: "One account's movements with its running balance.",
  tb: "Debits and credits per account at the period's end. They must foot.",
  is: "Revenue less expenses for the period.",
  bs: "What Milltech owns and owes on the period's last day.",
};

const TYPE_LABEL = {
  A: "Assets",
  L: "Liabilities",
  E: "Equity",
  R: "Revenue",
  X: "Expenses",
};

const JOURNALS = {
  SALES: "Sales Journal",
  CR: "Cash Receipts",
  CD: "Cash Disbursements",
  PUR: "Purchases Journal",
  GJ: "General Journal",
};

V.accounting = () => {
  const tab = ui.acc || "overview";
  const head = `<div class="top" style="margin-top:-12px"><span class="muted" style="font-size:13px">${ACC_SUB[tab]}</span><div class="row">${chip("EOPT + EIS", "acc")}${canInvoice() ? `<button class="pri" data-act="inv-new">New invoice</button>` : ""}</div></div>
   <div class="vtabs" role="tablist"><div>${ACC_TABS.map(([v, l]) => `<button role="tab" aria-selected="${v === tab}" data-seg="acc" data-val="${v}">${l}</button>`).join("")}</div></div>`;
  const body = {
    overview: accOverview,
    invoices: accInvoices,
    books: accBooks,
    bir: accBir,
  }[tab]();
  const open = ui.invOpen && S.invoices.find((i) => i.no === ui.invOpen);
  return head + body + (open ? invoiceSheet(open) : "");
};

function accOverview() {
  const yr = today().slice(0, 4),
    issued = S.invoices.filter(
      (i) => i.date.startsWith(yr) && i.status !== "VOID",
    ),
    unpaid = S.invoices.filter((i) => i.status === "UNPAID");
  const unbilled = S.projects.reduce(
      (a, p) => a + Math.max(0, p.recog - p.billed),
      0,
    ),
    pend = S.invoices.filter((i) => i.f2307 === "PENDING");
  const todo = [];
  if (can("gl.approve"))
    S.je
      .filter((j) => j.status === "DRAFT" && j.by !== ui.user)
      .forEach((j) =>
        todo.push([
          `Voucher ${j.id} to approve`,
          `${j.memo} · ${uname(j.by)}`,
          `<button data-act="jv-post" data-id="${j.id}">Approve & post</button>`,
        ]),
      );
  if (canInvoice())
    S.projects
      .filter((p) => p.recog - p.billed >= P(100000))
      .forEach((p) =>
        todo.push([
          `Bill ${p.code}: ${peso(p.recog - p.billed)} earned, not invoiced`,
          `${esc(client(p.cid)?.name || "")} · ${p.pct}% complete`,
          `<button data-act="inv-prep" data-id="${p.code}">Prepare invoice</button>`,
        ]),
      );
  if (canInvoice())
    S.runs.forEach((r) => {
      if (r.to >= today()) return;
      [...new Set(depActiveIn(r.from, r.to).map((d) => d.project))].forEach(
        (c) => {
          const key = c + "|" + r.from;
          if (S.depBilled[key]) return;
          const b = depActiveIn(r.from, r.to)
            .filter((d) => d.project === c)
            .reduce((a, d) => a + depPay(S, d, r.from, r.to).bill, 0);
          if (b)
            todo.push([
              `Bill site works for ${c}, ${shortD(r.from)} – ${fmtD(r.to)}: ${peso(b)}`,
              `${esc(client(proj(c).cid).name)} · 2% CWT (WC120)`,
              `<button data-act="dep-bill" data-id="${key}">Prepare invoice</button>`,
            ]);
        },
      );
    });
  [
    ...new Set(
      pend
        .filter((i) => qEnd(i.paidOn) < today())
        .map((i) => i.client + "|" + quarterOf(i.paidOn)),
    ),
  ].forEach((k) => {
    const [c, q] = k.split("|");
    todo.push([
      `Ask ${client(c).name} for its ${q} Form 2307`,
      `Needed to claim the tax withheld · due 20 days after the quarter`,
      `<button data-seg="acc" data-val="books" data-bsec="withholding">Open withholding</button>`,
    ]);
  });
  if (can("bir.view"))
    birCalendar()
      .filter((b) => b.due && !b.filed && daysTo(b.due) <= 14)
      .forEach((b) =>
        todo.push([
          `${b.form} · ${b.per} due ${fmtD(b.due)}`,
          b.amt != null ? peso(b.amt) : b.basis,
          can("bir.file")
            ? `<button data-act="file" data-id="${esc(b.k)}">Mark filed</button>`
            : "",
        ]),
      );
  const co = S.company,
    coOk =
      TIN_RE.test(co.tin || "") &&
      typeof co.address === "string" &&
      co.address.trim();
  return `<div class="tiles">${tile("Invoices issued, " + yr, pad2(issued.length), "g", "")}${tile("Outstanding", peso(unpaid.reduce((a, i) => a + i.due, 0)), unpaid.length ? "o" : "g")}${tile("Earned, not billed", peso(unbilled), unbilled ? "o" : "g")}${tile("2307s to collect", pad2([...new Set(pend.map((i) => i.client + quarterOf(i.paidOn)))].length), pend.length ? "r" : "g")}</div>
  <div class="grid"><div><div class="dh"><h2>To do<span class="cnt">${pad2(todo.length)}</span></h2></div><div class="inbox">${todo.length ? todo.map((t) => `<div><div><strong>${esc(t[0])}</strong><div class="small muted">${t[1]}</div></div><div class="row">${t[2]}</div></div>`).join("") : `<div><span class="muted">Nothing to do in the books.</span></div>`}</div></div>
   <div><div class="dh"><h2>Latest invoices<span class="cnt">${pad2(S.invoices.length)}</span></h2><button class="ghost link" data-seg="acc" data-val="invoices">See all invoices</button></div><div class="inbox">${S.invoices
     .slice(-5)
     .reverse()
     .map(
       (i) =>
         `<div><div><strong>${esc(i.no)}</strong><div class="small muted">${fmtD(i.date)}</div><div class="small"><span class="muted">Seller</span>${invoicePartySummary(invoiceParties(i).seller)}</div><div class="small"><span class="muted">Buyer</span>${invoicePartySummary(invoiceParties(i).buyer)}</div></div><div class="row"><span class="num">${peso(i.gross)}</span>${i.status === "PAID" ? chip("Paid", "good") : chip("Unpaid", "warn")}<button class="ghost" data-act="inv-view" data-id="${i.no}">View</button></div></div>`,
     )
     .join("")}</div></div></div>
  ${panel(
    "Registration",
    "What prints on every invoice: BIR Form 2303 details",
    canCompany() && !ui.coEdit
      ? `<button class="out" data-act="co-edit">Edit</button>`
      : "",
    ui.coEdit
      ? `<form id="coform" class="form"><label class="wide">Registered name<input id="co-name" value="${esc(co.name)}" required></label><label>Business style<input id="co-style" value="${esc(co.style)}"></label><label>VAT Reg. TIN<input id="co-tin" value="${esc(co.tin)}" placeholder="000-000-000-00000" pattern="${TIN_RE.source}" data-case="preserve" required></label><label>RDO<input id="co-rdo" value="${esc(co.rdo)}"></label><label class="wide">Registered business address<input id="co-addr" value="${esc(co.address)}" required></label>${ui.coErr ? `<p class="err wide">${esc(ui.coErr)}</p>` : ""}<div class="row wide"><button class="pri" type="submit">Save</button><button type="button" class="ghost" data-act="co-cancel">Cancel</button></div></form>`
      : `<dl class="party" style="display:grid;grid-template-columns:160px minmax(0,1fr);gap:6px 12px;margin:0"><dt class="lbl">Registered name</dt><dd style="margin:0">${esc(co.name)}</dd><dt class="lbl">VAT Reg. TIN</dt><dd class="num" style="margin:0;text-align:left">${esc(co.tin)}</dd><dt class="lbl">Business address</dt><dd style="margin:0">${esc(co.address)}</dd><dt class="lbl">RDO</dt><dd style="margin:0">${esc(co.rdo)}</dd></dl>${coOk ? "" : `<p class="err">Invoices are blocked until the TIN and address are complete.</p>`}<p class="small muted">Sample details. Replace them with the Certificate of Registration before issuing real invoices. Issued invoices keep the details they were printed with.</p>`,
  )}`;
}

function accBooks() {
  const sec = ui.bsec || "ledger",
    title = BOOK_SECTIONS.find((x) => x[0] === sec)[1];
  const inner = {
    copies: bkCopies,
    withholding: bkWithholding,
    ledger: bkLedger,
    payroll: bkPayroll,
    position: bkPosition,
    audit: () =>
      can("audit.view")
        ? V.audit()
        : `<p class="muted">The audit trail needs the audit permission.</p>`,
  }[sec]();
  return `<div><div class="ftabs" role="tablist">${BOOK_SECTIONS.map(([v, l]) => `<button role="tab" aria-selected="${v === sec}" data-seg="bsec" data-val="${v}">${l}</button>`).join("")}</div>
   <div class="fcard"><h2>${title}</h2><div class="fdesc">${BOOK_INTRO[sec]}</div>${inner}</div></div>`;
}

function bkCopies() {
  const t = S.invoices.reduce(
    (a, i) => ({
      net: a.net + i.net,
      vat: a.vat + i.vat,
      gross: a.gross + i.gross,
    }),
    { net: 0, vat: 0, gross: 0 },
  );
  return panel(
    "Sales book",
    `${S.invoices.length} invoices`,
    csvBtn("t-copies"),
    `<div class="tbl"><table id="t-copies"><thead><tr><th>Invoice</th><th>Date</th><th>Seller</th><th>Seller TIN</th><th>Seller address</th><th>Buyer</th><th>Buyer TIN</th><th>Buyer address</th><th class="n">VATable sales</th><th class="n">VAT</th><th class="n">Total</th></tr></thead><tbody>
   ${S.invoices.map((i) => `<tr><td class="num" style="text-align:left">${i.no}</td><td>${fmtD(i.date)}</td><td>${esc(invoiceParties(i).seller.name || "Not recorded")}</td><td class="num" style="text-align:left">${esc(invoiceParties(i).seller.tin || "Not recorded")}</td><td class="small">${esc(invoiceParties(i).seller.address || "Not recorded")}</td><td>${esc(invoiceParties(i).buyer.name || "Not recorded")}</td><td class="num" style="text-align:left">${esc(invoiceParties(i).buyer.tin || "Not recorded")}</td><td class="small">${esc(invoiceParties(i).buyer.address || "Not recorded")}</td><td class="n">${peso(i.net)}</td><td class="n">${peso(i.vat)}</td><td class="n">${peso(i.gross)}</td></tr>`).join("")}
   </tbody><tfoot><tr><td colspan="8">Totals</td><td class="n">${peso(t.net)}</td><td class="n">${peso(t.vat)}</td><td class="n">${peso(t.gross)}</td></tr></tfoot></table></div>`,
  );
}

function bkWithholding() {
  const paid = S.invoices.filter((i) => i.status === "PAID"),
    tot = paid.reduce((a, i) => a + i.cwt, 0),
    miss = paid.filter((i) => i.f2307 === "PENDING");
  return `<div class="tiles">${tile("Withheld by clients", peso(tot), "g")}${tile("2307s received", pad2(paid.length - miss.length), "g")}${tile("2307s awaited", pad2(miss.length), miss.length ? "r" : "g")}</div>
  ${panel(
    "Creditable withholding (2307)",
    "15% on consultancy fees (WC011), 2% on manpower and site works (WC120). Click Mark received when the client's certificate arrives.",
    csvBtn("t-2307"),
    `<div class="tbl"><table id="t-2307"><thead><tr><th>Seller · TIN · business address</th><th>Client · TIN · business address</th><th>Invoice</th><th>Paid</th><th>Quarter</th><th class="n">Tax withheld</th><th>Form 2307</th><th></th></tr></thead><tbody>
   ${paid
     .slice()
     .reverse()
     .map(
       (i) =>
         `<tr><td>${invoicePartySummary(invoiceParties(i).seller)}</td><td>${invoicePartySummary(invoiceParties(i).buyer)}</td><td class="num" style="text-align:left">${esc(i.no)}<div class="small muted">${i.atc || "WC011"}</div></td><td>${fmtD(i.paidOn)}</td><td>${quarterOf(i.paidOn)}</td><td class="n">${peso(i.cwt)}</td><td>${i.f2307 === "RECEIVED" ? chip("Received", "good") : chip("Awaited", "bad")}</td><td>${i.f2307 !== "RECEIVED" && canInvoice() ? `<button data-act="f2307" data-id="${i.no}">Mark received</button>` : ""}</td></tr>`,
     )
     .join("")}
   </tbody><tfoot><tr><td colspan="5">Total</td><td class="n">${peso(tot)}</td><td colspan="2"></td></tr></tfoot></table></div><p class="small muted">Each 2307 goes into the SAWT attached to the 1702Q. A credit without its certificate is disallowed.</p>`,
  )}`;
}

function bkLedger() {
  const v = ui.lv || "vouchers",
    dated = ["books", "activity", "tb", "is", "bs"].includes(v);
  const body = {
    chart: lgChart,
    vouchers: lgVouchers,
    books: lgBooks,
    activity: lgActivity,
    tb: lgTB,
    is: lgIS,
    bs: lgBS,
  }[v]();
  return `${seg("lv", LEDGER_VIEWS, v)}<span class="small muted" style="font-size:12px">${LEDGER_INTRO[v]}</span>${dated ? periodSeg() : ""}${body}`;
}

function lgChart() {
  return panel(
    "Chart of accounts",
    `${ACCOUNTS.length} accounts`,
    csvBtn("t-coa"),
    `<div class="tbl"><table id="t-coa"><thead><tr><th>Code</th><th>Account</th><th class="n">Balance today</th></tr></thead><tbody>
   ${["A", "L", "E", "R", "X"]
     .map(
       (ty) =>
         `<tr class="grp"><td colspan="3">${TYPE_LABEL[ty]}</td></tr>` +
         ACCOUNTS.filter((a) => a[2] === ty)
           .map(
             (a) =>
               `<tr><td class="num" style="text-align:left">${a[0]}</td><td>${esc(a[1])}${a[3] ? ` <span class="small muted">contra</span>` : ""}</td><td class="n">${peso(natural(a[0]))}</td></tr>`,
           )
           .join(""),
     )
     .join("")}
   </tbody></table></div>`,
  );
}

function lgVouchers() {
  const u = me(),
    q = (ui.q || "").toLowerCase(),
    f = ui.glf || "all";
  const match = (j) =>
    (f === "all" ||
      (f === "draft" && j.status === "DRAFT") ||
      (f === "posted" && j.status === "POSTED") ||
      (f === "auto" && j.src !== "manual")) &&
    (!q || (j.id + " " + j.memo).toLowerCase().includes(q));
  const list = [...S.je].reverse().filter(match),
    drafts = S.je.filter((j) => j.status === "DRAFT").length;
  return `${ui.jvDraft ? jvForm() : ""}${panel(
    "Journal vouchers",
    `Books locked through ${fmtD(S.lockedThrough)}`,
    `${can("period.lock") ? `<button class="out" data-act="lock">Lock through 30 Sep 2026</button>` : ""}${can("gl.create") ? `<button class="out" data-act="jv-new">New voucher</button>` : ""}${csvBtn("t-jv")}`,
    `<div class="row" style="justify-content:space-between">${seg(
      "glf",
      [
        ["all", "All entries"],
        ["draft", `To approve${drafts ? ` (${drafts})` : ""}`],
        ["posted", "Posted"],
        ["auto", "From invoices & payroll"],
      ],
      f,
    )}<input id="gl-q" type="search" placeholder="Search number or particulars" value="${esc(ui.q || "")}" style="flex:0 1 280px" aria-label="Search vouchers"></div>
   <div class="tbl"><table id="t-jv"><thead><tr><th>No.</th><th>Date</th><th>Particulars</th><th class="n">Amount</th><th>Status</th><th>Prepared / approved</th><th></th></tr></thead><tbody>
   ${
     list
       .map((j) => {
         const tot = j.lines.reduce((a, l) => a + l.d, 0),
           act =
             j.status === "DRAFT" && can("gl.approve")
               ? j.by === u.id
                 ? `<span class="small muted">You prepared this</span>`
                 : `<button data-act="jv-post" data-id="${j.id}">Approve & post</button>`
               : "";
         return `<tr><td class="num" style="text-align:left">${j.id}</td><td>${fmtD(j.date)}</td><td>${esc(j.memo)}<details class="lines"><summary>${j.lines.length} lines · ${JOURNALS[journalOf(j)]}</summary><table class="mini"><tbody>${j.lines.map((l) => `<tr><td>${l.a} ${esc(ACC[l.a].name)}</td><td class="n">${l.d ? peso(l.d) : ""}</td><td class="n">${l.c ? peso(l.c) : ""}</td></tr>`).join("")}</tbody></table></details></td><td class="n">${peso(tot)}</td><td>${ssn(j.status)}</td><td class="small">${esc(uname(j.by))}<br>${esc(j.ap ? uname(j.ap) : "—")}</td><td>${act}</td></tr>`;
       })
       .join("") ||
     `<tr><td colspan="7" class="muted">No vouchers match. Clear the search or pick another filter.</td></tr>`
   }
   </tbody></table></div>`,
  )}`;
}

function lgBooks() {
  const [from, to] = periodRange(),
    inP = S.je.filter(
      (j) => j.status === "POSTED" && j.date >= from && j.date <= to,
    ),
    b = ui.jb || "SALES",
    list = inP.filter((j) => journalOf(j) === b);
  return `<div class="tiles">${Object.entries(JOURNALS)
    .map(([k, l]) => {
      const es = inP.filter((j) => journalOf(j) === k);
      return `<button class="tile" data-seg="jb" data-val="${k}" style="${k === b ? "background:var(--tile-on)" : ""}"><span class="top-r"><span class="dot ${es.length ? "g" : ""}"></span></span><span><span class="v" style="display:block">${pad2(es.length)}</span><span class="lbl">${l} · ${pesoShort(es.reduce((a, j) => a + j.lines.reduce((x, y) => x + y.d, 0), 0))}</span></span></button>`;
    })
    .join("")}</div>
  ${panel(JOURNALS[b], `${fmtD(from)} – ${fmtD(to)}`, csvBtn("t-jb"), `<div class="tbl"><table id="t-jb"><thead><tr><th>Date</th><th>No.</th><th>Account</th><th class="n">Debit</th><th class="n">Credit</th></tr></thead><tbody>${list.map((j) => `<tr class="grp"><td colspan="5">${fmtD(j.date)} · ${j.id} · ${esc(j.memo)}</td></tr>` + j.lines.map((l) => `<tr><td></td><td></td><td>${l.a} ${esc(ACC[l.a].name)}</td><td class="n">${l.d ? peso(l.d) : ""}</td><td class="n">${l.c ? peso(l.c) : ""}</td></tr>`).join("")).join("") || `<tr><td colspan="5" class="muted">No entries in this book for the period.</td></tr>`}</tbody></table></div>`)}`;
}

function lgActivity() {
  const [from, to] = periodRange(),
    a = ui.actAcc || "1020",
    nat = ACC[a].type === "A" || ACC[a].type === "X" ? 1 : -1;
  let run = nat * bal(a, addDays(from, -1));
  const open = run;
  const rows = S.je
    .filter((j) => j.status === "POSTED" && j.date >= from && j.date <= to)
    .sort((x, y) => x.date.localeCompare(y.date))
    .flatMap((j) =>
      j.lines
        .filter((l) => l.a === a)
        .map((l) => {
          run += nat * (l.d - l.c);
          return `<tr><td>${fmtD(j.date)}</td><td class="num" style="text-align:left">${j.id}</td><td>${esc(j.memo)}</td><td class="n">${l.d ? peso(l.d) : ""}</td><td class="n">${l.c ? peso(l.c) : ""}</td><td class="n">${peso(run)}</td></tr>`;
        }),
    );
  return panel(
    `${a} ${esc(ACC[a].name)}`,
    `${fmtD(from)} – ${fmtD(to)}`,
    `<select id="act-acc" aria-label="Account">${ACCOUNTS.map((x) => `<option value="${x[0]}"${x[0] === a ? " selected" : ""}>${x[0]} ${esc(x[1])}</option>`).join("")}</select>${csvBtn("t-act")}`,
    `<div class="tbl"><table id="t-act"><thead><tr><th>Date</th><th>No.</th><th>Particulars</th><th class="n">Debit</th><th class="n">Credit</th><th class="n">Balance</th></tr></thead><tbody><tr class="grp"><td colspan="5">Opening balance</td><td class="n">${peso(open)}</td></tr>${rows.join("") || `<tr><td colspan="6" class="muted">No movements in the period.</td></tr>`}</tbody><tfoot><tr><td colspan="5">Closing balance</td><td class="n">${peso(run)}</td></tr></tfoot></table></div>`,
  );
}

function lgTB() {
  const to = periodRange()[1],
    rows = ACCOUNTS.map((a) => ({ a, b: bal(a[0], to) })).filter((r) => r.b),
    dr = rows.reduce((x, r) => x + Math.max(r.b, 0), 0),
    cr = rows.reduce((x, r) => x + Math.max(-r.b, 0), 0);
  return panel(
    `Trial balance at ${fmtD(to)}`,
    "",
    `${dr === cr ? chip("Debits equal credits", "good") : chip("Out of balance", "bad")}${csvBtn("t-tb")}`,
    `<div class="tbl"><table id="t-tb"><thead><tr><th>Code</th><th>Account</th><th class="n">Debit</th><th class="n">Credit</th></tr></thead><tbody>
   ${["A", "L", "E", "R", "X"]
     .map((ty) => {
       const rs = rows.filter((r) => r.a[2] === ty);
       return rs.length
         ? `<tr class="grp"><td colspan="4">${TYPE_LABEL[ty]}</td></tr>` +
             rs
               .map(
                 (r) =>
                   `<tr><td class="num" style="text-align:left">${r.a[0]}</td><td>${esc(r.a[1])}</td><td class="n">${r.b > 0 ? peso(r.b) : ""}</td><td class="n">${r.b < 0 ? peso(-r.b) : ""}</td></tr>`,
               )
               .join("")
         : "";
     })
     .join("")}
   </tbody><tfoot><tr><td></td><td>Totals</td><td class="n">${peso(dr)}</td><td class="n">${peso(cr)}</td></tr></tfoot></table></div>`,
  );
}

function lgIS() {
  const [from, to] = periodRange(),
    line = (a) => natural(a[0], to, from),
    rev = ACCOUNTS.filter((a) => a[2] === "R"),
    cos = ACCOUNTS.filter((a) => COS.includes(a[0])),
    opx = ACCOUNTS.filter((a) => a[2] === "X" && !COS.includes(a[0]));
  const sum = (l) => l.reduce((x, a) => x + line(a), 0),
    R = sum(rev),
    C = sum(cos),
    O = sum(opx);
  const grp = (t, l) =>
    `<tr class="grp"><td colspan="3">${t}</td></tr>` +
    l
      .map(
        (a) =>
          `<tr><td class="num" style="text-align:left">${a[0]}</td><td>${esc(a[1])}</td><td class="n">${peso(line(a))}</td></tr>`,
      )
      .join("");
  return panel(
    "Income statement",
    `${fmtD(from)} – ${fmtD(to)}`,
    csvBtn("t-is"),
    `<div class="tbl"><table id="t-is"><thead><tr><th>Code</th><th>Account</th><th class="n">Amount</th></tr></thead><tbody>${grp("Revenue", rev)}${grp("Cost of services", cos)}<tr><td></td><td><strong>Gross profit</strong></td><td class="n"><strong>${peso(R - C)}</strong></td></tr>${grp("Operating expenses", opx)}</tbody><tfoot><tr><td></td><td>Net income before tax</td><td class="n">${peso(R - C - O)}</td></tr></tfoot></table></div>`,
  );
}

function lgBS() {
  const to = periodRange()[1],
    y0 = to.slice(0, 4) + "-01-01",
    ni =
      ACCOUNTS.filter((a) => a[2] === "R").reduce(
        (x, a) => x + natural(a[0], to, y0),
        0,
      ) -
      ACCOUNTS.filter((a) => a[2] === "X").reduce(
        (x, a) => x + natural(a[0], to, y0),
        0,
      );
  const grp = (ty) => {
    const l = ACCOUNTS.filter((a) => a[2] === ty && bal(a[0], to));
    return (
      `<tr class="grp"><td colspan="3">${TYPE_LABEL[ty]}</td></tr>` +
      l
        .map(
          (a) =>
            `<tr><td class="num" style="text-align:left">${a[0]}</td><td>${esc(a[1])}</td><td class="n">${peso(natural(a[0], to))}</td></tr>`,
        )
        .join("")
    );
  };
  const A = ACCOUNTS.filter((a) => a[2] === "A").reduce(
      (x, a) => x + natural(a[0], to),
      0,
    ),
    L = ACCOUNTS.filter((a) => a[2] === "L").reduce(
      (x, a) => x + natural(a[0], to),
      0,
    ),
    E = ACCOUNTS.filter((a) => a[2] === "E").reduce(
      (x, a) => x + natural(a[0], to),
      0,
    );
  return panel(
    `Balance sheet at ${fmtD(to)}`,
    "",
    `${A === L + E + ni ? chip("Assets = liabilities + equity", "good") : chip("Does not balance", "bad")}${csvBtn("t-bs")}`,
    `<div class="tbl"><table id="t-bs"><thead><tr><th>Code</th><th>Account</th><th class="n">Amount</th></tr></thead><tbody>${grp("A")}<tr><td></td><td><strong>Total assets</strong></td><td class="n"><strong>${peso(A)}</strong></td></tr>${grp("L")}${grp("E")}<tr><td></td><td>Net income, current year</td><td class="n">${peso(ni)}</td></tr></tbody><tfoot><tr><td></td><td>Total liabilities and equity</td><td class="n">${peso(L + E + ni)}</td></tr></tfoot></table></div>`,
  );
}

function bkPayroll() {
  const m = today().slice(0, 7),
    runs = S.runs.filter(
      (r) => r.pay.slice(0, 7) === m && r.status !== "DRAFT",
    ),
    t = (k) => runs.reduce((a, r) => a + runTot(r)[k], 0),
    f = birCalendar();
  const due = (k) => f.find((x) => x.k === k + "|" + m)?.due,
    rows = [
      ["SSS & EC", t("sssEE"), t("sssER") + t("ec"), due("SSS"), "RA 11199"],
      ["PhilHealth", t("phEE"), t("phER"), due("PHIC"), "RA 11223"],
      ["Pag-IBIG", t("hdEE"), t("hdER"), due("HDMF"), "RA 9679"],
      ["BIR 1601-C", t("tax"), 0, due("1601-C"), "NIRC §58(A)"],
    ];
  return panel(
    `Remittances for ${new Date(m + "-01T00:00:00").toLocaleDateString("en-PH", { month: "long", year: "numeric" })}`,
    `From ${runs.length} approved payroll run${runs.length === 1 ? "" : "s"}; draft runs are not counted`,
    `${can("pay.view") ? `<button class="out" data-go="payroll">Open payroll</button>` : ""}${csvBtn("t-rem")}`,
    `<div class="tbl"><table id="t-rem"><thead><tr><th>Agency</th><th class="n">Employee share</th><th class="n">Employer share</th><th class="n">Total</th><th>Due</th><th>Basis</th></tr></thead><tbody>${rows.map((r) => `<tr><td>${r[0]}</td><td class="n">${peso(r[1])}</td><td class="n">${peso(r[2])}</td><td class="n">${peso(r[1] + r[2])}</td><td>${r[3] ? fmtD(r[3]) : "—"}</td><td class="small muted">${r[4]}</td></tr>`).join("")}</tbody><tfoot><tr><td>Total</td><td class="n">${peso(rows.reduce((a, r) => a + r[1], 0))}</td><td class="n">${peso(rows.reduce((a, r) => a + r[2], 0))}</td><td class="n">${peso(rows.reduce((a, r) => a + r[1] + r[2], 0))}</td><td colspan="2"></td></tr></tfoot></table></div>`,
  );
}

function bkPosition() {
  const f = taxFigures(),
    R = (k, v, s) =>
      `<tr><td>${k}</td><td class="n">${s ? `<strong>${peso(v)}</strong>` : peso(v)}</td></tr>`;
  return `<div class="grid">${panel("2550Q · Q3 2026 VAT", "From accounts 2240 and 1410 at 30 Sep", "", `<div class="tbl"><table style="min-width:0"><tbody>${R("Output VAT", f.outVat)}${R("Less input VAT", f.inVat)}</tbody><tfoot><tr><td>VAT payable</td><td class="n">${peso(f.vatDue)}</td></tr></tfoot></table></div><p class="small muted">VAT on services falls due when the invoice is issued (EOPT, RA 11976), so unbilled revenue carries no output VAT until billed. Government clients withhold 5% final VAT (NIRC §114(C)).</p>`)}
   ${panel(
     "1702Q · Q3 2026 income tax",
     `${f.rate * 100}% regular corporate income tax`,
     "",
     `<div class="tbl"><table style="min-width:0"><tbody>${R("Consultancy fees earned", f.rev)}${R("Less cost of services", f.cogs)}${R("Less operating expenses", f.opex)}${R("Taxable income, 9 months", f.nti, 1)}${R(`RCIT at ${f.rate * 100}%`, f.rcit)}${R("MCIT 2% of gross income (comparison)", f.mcit)}${R("Less CWT from client 2307s", f.cwt)}${R("Less Q1–Q2 payments", f.prior)}</tbody><tfoot><tr><td>${f.itDue < 0 ? "Excess credits, nothing to pay" : "Payable this quarter"}</td><td class="n">${peso(Math.abs(f.itDue))}</td></tr></tfoot></table></div>
    <p class="small muted">The 20% rate needs taxable income of ₱5M or less and assets of ₱100M or less excluding land (RA 11534). Projected full-year income is ${peso(f.projected)}. ${f.itDue < 0 ? "The excess credit carries forward; on the 1702-RT the choice of carry-over or refund is irrevocable (NIRC §76)." : ""}</p>`,
   )}</div>`;
}

function accBir() {
  const all = birCalendar(),
    bf = ui.birf || "open",
    cal = all.filter(
      (b) => bf === "all" || (bf === "open" ? !b.filed : !!b.filed),
    );
  return panel(
    "Filing calendar",
    "eBIRForms deadlines; weekends and holidays roll forward. eFPS filers follow their group schedule (RR 26-2002).",
    csvBtn("t-bir"),
    `${seg(
      "birf",
      [
        ["open", `To do (${all.filter((b) => !b.filed).length})`],
        ["filed", `Filed (${all.filter((b) => b.filed).length})`],
        ["all", "All of 2026"],
      ],
      bf,
    )}
   <div class="tbl"><table id="t-bir"><thead><tr><th>Return</th><th>Period</th><th>Due</th><th class="n">Amount from books</th><th>Status</th><th>Basis</th><th></th></tr></thead><tbody>
   ${
     cal
       .map((b) => {
         const d = b.due ? daysTo(b.due) : 0,
           st = b.filed
             ? chip("Filed " + fmtD(b.filed), "good")
             : !b.due
               ? chip("After annual meeting", "mut")
               : d < 0
                 ? chip("Overdue", "bad")
                 : d <= 7
                   ? chip(`Due in ${d} day${d === 1 ? "" : "s"}`, "warn")
                   : chip(`In ${d} days`, "mut");
         return `<tr><td><strong>${esc(b.form)}</strong></td><td>${esc(b.per)}</td><td>${b.due ? fmtD(b.due) : "—"}</td><td class="n">${b.amt == null ? "—" : peso(b.amt)}</td><td>${st}</td><td class="small muted">${esc(b.basis)}</td><td>${!b.filed && b.due && can("bir.file") ? `<button data-act="file" data-id="${esc(b.k)}">Mark filed</button>` : ""}</td></tr>`;
       })
       .join("") || `<tr><td colspan="7" class="muted">Nothing here.</td></tr>`
   }
   </tbody></table></div>`,
  );
}

export {
  ACC_TABS,
  ACC_SUB,
  BOOK_SECTIONS,
  BOOK_INTRO,
  LEDGER_VIEWS,
  LEDGER_INTRO,
  TYPE_LABEL,
  JOURNALS,
  accOverview,
  accBooks,
  bkCopies,
  bkWithholding,
  bkLedger,
  lgChart,
  lgVouchers,
  lgBooks,
  lgActivity,
  lgTB,
  lgIS,
  lgBS,
  bkPayroll,
  bkPosition,
  accBir,
};
